import { c as _c } from "react/compiler-runtime";
import { showNotification } from '@mantine/notifications';
import { ForecastGroupKey, ResolutionForecasts, UseForecastGroup, ForecastQuestionCfgAmount } from 'shared/forecast/forecast';
import { ForecastCfg, isLogisticValid } from 'shared/forecast/forecastTypes';
import { QuestionForecastType } from 'shared/forecast/question';
import { Logistic } from 'shared/forecast/forecast';
import { Fmt } from 'shared/forecast';
import { compactString } from 'shared/text';
import { doSafe, doTo } from 'shared/promise';
import { concat, compactObj, notNullOrEmpty } from 'shared/collection';
import { isNullOrEmpty } from 'shared/collection';
import { Dist, DistDrawn } from 'shared/chart';
import { zForecast, zForecastFetchedBase } from 'shared/forecast';
import { showError } from 'components/notify';
import { usePortal } from 'components/portal/portal';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { first, isEmpty, mapValues, pick, values } from 'remeda';
import { match, P } from 'ts-pattern';
import { Json, NotNull, notNull } from 'shared/type';
import { Forecast as F } from 'shared/forecast/forecast';
import { Database } from 'utils/db/tables.generated';
import { useQuerySb } from 'utils/db/useQuerySb';
import { useCallback } from 'react';
import { z } from 'zod';
import { UseComments } from 'components/discussion/comments';
import { QuestionContextProps } from './questionComponentTypes';
import { sb } from 'utils/db';
import { useLatest } from 'components/hooks';
import { sbAll } from 'utils/db/query';
type ForecastRow = Database['public']['Tables']['forecast']['Row'];
type ForecastFilter = {
  question_id: string;
  timestamps?: Array<number | undefined>;
  subquestion_id?: string;
} & Partial<Pick<ForecastRow, 'created_by'>>;
const zForecastFetched = zForecastFetchedBase.extend({
  created_at: z.string(),
  created_by: z.string()
});
export type ForecastFetched = z.infer<typeof zForecastFetched>;
export const fetchForecasts = async (filter: ForecastFilter | undefined): Promise<Array<ForecastFetched> | undefined> => {
  if (!filter) return undefined;
  const {
    question_id: q_id,
    timestamps,
    ...otherFilters
  } = filter;
  const queryRes = await sbAll(sb.rpc('forecast_at_ts', {
    q_id,
    timestamps: timestamps as number[] | undefined,
    filter: isNullOrEmpty(otherFilters) ? undefined : compactObj(otherFilters) as Json
  }));
  if (!queryRes.ok) throw Error(queryRes.message);
  return queryRes.map(d => match(zForecastFetched.safeParse(d)).with({
    success: true
  }, ({
    data
  }) => data).with({
    success: false
  }, ({
    error
  }) => {
    console.error('error loading forecast cfg', error.message, d);
    return undefined;
  }).run()).filter(notNull);
};
const forecastNaturalId = (f: ForecastFetched) => `${f.question_id}|${f.subquestion_id ?? ''}|${f.created_by}|${f.cfg.code}`;
export const useForecastsQuery = t0 => {
  const $ = _c(14);
  const {
    filter,
    disabled,
    debugName
  } = t0;
  const {
    question_id,
    subquestion_id,
    timestamps
  } = filter;
  let t1;
  if ($[0] !== timestamps) {
    t1 = doSafe(timestamps?.filter(notNullOrEmpty), _temp);
    $[0] = timestamps;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== question_id || $[3] !== t1) {
    t2 = ["useForecasts", {
      question_id,
      timestamps: t1
    }];
    $[2] = question_id;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== question_id || $[6] !== timestamps) {
    t3 = async () => await fetchForecasts({
      question_id,
      timestamps
    });
    $[5] = question_id;
    $[6] = timestamps;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = filter != null && !disabled;
  let t5;
  if ($[8] !== subquestion_id) {
    t5 = data => data?.filter(f => subquestion_id === undefined || f.subquestion_id == subquestion_id);
    $[8] = subquestion_id;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== debugName || $[11] !== t4 || $[12] !== t5) {
    t6 = {
      enabled: t4,
      staleTime: 60000,
      refetchDebounce: 5000,
      select: t5,
      debugName
    };
    $[10] = debugName;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const query = useQuerySb("forecast", t2, t3, t6);
  return query;
};
export const useForecasts = ({
  filter,
  disabled,
  debugName
}: {
  filter: ForecastFilter;
  disabled?: boolean;
  debugName?: string;
}): UseForecasts => {
  const {
    profile
  } = usePortal();
  const query = useForecastsQuery({
    filter,
    disabled,
    debugName
  });
  const latest = useLatest({
    query
  });
  const saveForecast = useCallback(async (cfg: ForecastCfg, {
    question_id,
    subquestion_id
  }: SaveForecastOptions): Promise<ForecastFetched | undefined> => {
    if (!profile) throw Error('profile required');
    const toSave = {
      id: undefined,
      question_id,
      subquestion_id,
      cfg: zForecast.parse(cfg) as Json,
      created_by: profile.id,
      created_at: DateTime.utc().toISO(),
      workspace_id: profile.workspace_id
    };
    const insertRes = await sb.from('forecast').insert(toSave).select().single();
    return match(insertRes).with({
      error: {}
    }, ({
      error: {
        message,
        details
      }
    }) => {
      showError(`${message}: ${details}`, 'Unable to save forecast');
      return undefined;
    }).otherwise(({
      data
    }) => {
      const updated = zForecastFetched.parse({
        ...data,
        workspace_id: profile.workspace_id
      });
      const nid = forecastNaturalId(updated);
      latest.current.query.setQueryData(forecasts => concat(...(forecasts ?? []).filter(f => forecastNaturalId(f) != nid), updated.cfg.deleted ? undefined : updated));
      showNotification({
        message: 'Forecast submitted'
      });
      return updated;
    });
  }, [profile]);
  const {
    data: forecasts_0,
    isLoading: forecastsLoading,
    isFetching: forecastsFetching
  } = query;
  const {
    byGroup: byGroup_0,
    forecastGroups
  } = useMemo(() => {
    const byGroup = forecasts_0 != null && profile != null ? mapValues(F.group({
      forecasts: forecasts_0,
      isMine: f_0 => f_0.created_by == profile.id
    }), g => ({
      ...g,
      myForecast: profile ? first(g.forecasts?.filter(({
        created_by
      }) => created_by == profile.id) ?? []) as ForecastFetched | undefined : undefined,
      save: (cfg_0, options) => saveForecast(cfg_0, {
        ...options,
        ...pick(g.group, ['question_id', 'subquestion_id'])
      })
    }) satisfies UseForecastGroupApp) : {};
    return {
      byGroup,
      forecastGroups: values(byGroup ?? {})
    };
  }, [forecasts_0, profile]);
  const getForecasts = useCallback((group: ForecastGroupKey) => forecasts_0 == null ? undefined : {
    ...F.getForecasts(byGroup_0, group),
    save: group.question_id == null ? undefined : (cfg_1, options_0) => saveForecast(cfg_1, {
      ...options_0,
      ...pick(group, ['question_id', 'subquestion_id'])
    })
  } as UseForecastGroupApp, [byGroup_0, saveForecast]);
  return {
    getForecasts,
    saveForecast,
    forecastGroups,
    forecastsLoading,
    forecastsFetching
  };
};
export type UseForecasts = {
  getForecasts: (group: ForecastGroupKey) => UseForecastGroupApp | undefined;
  saveForecast: (cfg: ForecastCfg, options: SaveForecastOptions & {}) => void;
  forecastGroups: UseForecastGroupApp[];
  forecastsLoading: boolean;
  forecastsFetching: boolean;
};
type SaveForecastOptions = {
  post?: boolean;
  question_id: string;
  subquestion_id?: string;
  previous?: ForecastCfg;
};
export type UseForecastGroupApp = UseForecastGroup<ForecastFetched> & {
  save: (cfg: ForecastCfg, options?: Pick<SaveForecastOptions, 'post' | 'previous'>) => Promise<ForecastFetched | undefined>;
};
export type UseForecastItem = NotNull<UseForecastGroupApp['forecasts']>[number];
export const forecastCommentOnSave = async ({
  newId,
  question_id,
  cfg,
  resolution,
  uComments: {
    post,
    comments
  }
}: {
  newId: string;
  question_id: string;
  cfg: ForecastCfg;
  resolution: Pick<ResolutionForecasts, 'group' | 'cfg' | 'resolution'>;
  uComments: Pick<UseComments, 'post' | 'comments'>;
}) => {
  const forecastSummary = (cfg: ForecastCfg, o?: {
    hideUnits?: boolean;
  }) => {
    const q = resolution.cfg as ForecastQuestionCfgAmount;
    const amountFormat = () => Fmt.getOption({
      forecastType: 'amount',
      numberFormat: q.numberFormat,
      unitLabel: o?.hideUnits ? '' : q.unitLabel ?? '',
      mode: 'absolute',
      amountType: q.amountType
    }).default;
    return match(cfg).with({
      type: 'likelihood'
    }, f => doSafe(f.relativePoints != null ? f.relativePoints : f.likelihood, v => Fmt.fmt(v, Fmt.getOption({
      forecastType: 'likelihood',
      mode: f.relativePoints != null ? 'relative' : 'absolute'
    }).default))).with({
      type: 'logistic'
    }, f => isLogisticValid(f) ? doSafe(Logistic.get50thFromCfg({
      f,
      q
    }), v => Fmt.fmt(v, amountFormat())) ?? '' : '').with({
      type: 'drawn'
    }, pd => pd ? doSafe(Dist.pdfToDiscrete(DistDrawn.cfgToPdf(pd))?.percentile50, v => Fmt.fmt(v, amountFormat())) ?? '' : '').exhaustive();
  };
  const previous = resolution.group.myForecast;
  const previousText = previous ? forecastSummary(previous?.cfg, {
    hideUnits: true
  }) : undefined;
  const currentText = forecastSummary(cfg);
  const text = `${doTo(compactString(resolution.resolution.labelShort) ?? resolution.resolution.label, l => l ? `*${l}* ` : '')}${previous ? previousText == currentText ? `**${currentText}**` : `**${previousText ?? ''}** -> **${currentText}**` : `**${currentText}**`}. ${cfg.notes ?? ''}`;
  const previousComment = previous ? comments?.roots.find(c => c.cfg.type == 'forecast-note' && c.cfg.forecast_id == previous.id) : undefined;

  // update existing comment if the values haven't changed
  const updateExisting = previousComment != null && previousText != null && previousText == currentText && cfg.notes != null;
  await post(updateExisting ? {
    id: previousComment.id,
    cfg: {
      ...previousComment.cfg,
      text,
      forecast_id: newId
    }
  } : {
    question_id,
    cfg: {
      text,
      status: [],
      type: 'forecast-note',
      forecast_id: newId
    },
    subquestion_id: resolution.group?.group.subquestion_id
  });
};
function _temp(ts) {
  return isEmpty(ts) ? undefined : ts;
}